import { DEFAULT_CURRENCY } from '@constants/global';

export const numberFormatCache = new Map<string, Intl.NumberFormat>();

export interface FormatOptions {
  number: number;
  currency?: string;
  shortened?: boolean;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  removeSpaces?: boolean;
  showPlusPrefix?: boolean;
  showNegativePrefix?: boolean;
}

// All currencies use Anglo-Saxon format: symbol before number, comma thousands, period decimal (e.g., €1,234.56)
const LOCALE = 'en-US';

const createCacheKey = (
  currency: string,
  minFractionDigits: number,
  maxFractionDigits: number,
  shortened: boolean
): string => {
  return `${currency}-${minFractionDigits}-${maxFractionDigits}-${shortened}`;
};

export const formatCurrency = (options: FormatOptions): string => {
  const currency = options.currency ?? DEFAULT_CURRENCY;

  const minFractionDigits = options.minimumFractionDigits === 0 ? 0 : options.minimumFractionDigits ?? 2;
  const maxFractionDigits =
    options.maximumFractionDigits === undefined
      ? Math.max(2, minFractionDigits)
      : Math.max(options.maximumFractionDigits, minFractionDigits);

  const cacheKey = createCacheKey(currency, minFractionDigits, maxFractionDigits, options.shortened ?? false);

  let formatter = numberFormatCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      notation: options.shortened ? 'compact' : 'standard',
    });
    numberFormatCache.set(cacheKey, formatter);
  }

  let result: string;

  // en-US locale always places symbol before number for all currencies
  if (options.removeSpaces === false) {
    result = formatter.format(options.number);
    // Normalize non-breaking spaces to regular spaces
    result = result.replaceAll(/[\u00A0\u202F]/g, ' ');
  } else {
    // Use formatToParts to skip 'literal' parts (spaces between symbol and number)
    // while keeping 'group' separators (commas), decimals, etc.
    const parts = formatter.formatToParts(options.number);
    result = parts
      .filter((p) => p.type !== 'literal')
      .map((p) => p.value)
      .join('');
  }

  // Add plus prefix for positive numbers if requested
  if (options.showPlusPrefix && options.number > 0) {
    result = `+${result}`;
  }

  if (options.showNegativePrefix && options.number >= 0) {
    result = `-${result}`;
  }

  return result;
};

/**
 * Formats a plain number (no currency). Always Anglo format: 1,234
 */
export const formatValue = (options: FormatOptions): string => {
  const minFractionDigits = options.minimumFractionDigits ?? 0;
  const maxFractionDigits =
    options.maximumFractionDigits === undefined
      ? Math.max(0, minFractionDigits)
      : Math.max(options.maximumFractionDigits, minFractionDigits);

  const key = `decimal-${minFractionDigits}-${maxFractionDigits}-${options.shortened ?? false}`;

  let formatter = numberFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'decimal',
      useGrouping: true,
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      notation: options.shortened ? 'compact' : 'standard',
    });
    numberFormatCache.set(key, formatter);
  }

  return formatter.format(options.number);
};
